/*! JD - EMPIRE Theme | Copyright (c) 2026 JDA Commerce. All rights reserved. */

/**
 * Size normalisation.
 *
 * Shopify option values are typed by hand, so a single store ends up with all
 * of these for the same six sizes:
 *
 *   S, M, L, XL, 2XL, 3XL
 *   S, M, L, XL, XXL, 3XL
 *   S(4–6), M(8-10), L(12-14), XL(16-18), 2XL(20-22), 3XL(24-26)
 *   S, M (Almost sold out), L(Almost sold out), XL
 *
 * and for shoes:
 *
 *   6, 7, 7.5, 8 …
 *   4 US | 35 EU, 5 US | 36 EU …
 *   EU 34 / US 4, EU 35 / US 5 …
 *   US WOMEN 6, US WOMEN 8/8.5 …
 *
 * Everything here turns those into one canonical form so the fit finder can
 * recognise a product without the merchant having to tidy the catalogue first.
 * Exposed on window so both the fit finder and anything added later can use it.
 */
(() => {
  const LADDER = ['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];

  const APPAREL_ALIASES = {
    XXS: '2XS', '2XS': '2XS',
    XS: 'XS', 'EXTRA SMALL': 'XS', 'X-SMALL': 'XS', XSMALL: 'XS',
    S: 'S', SMALL: 'S',
    M: 'M', MEDIUM: 'M', MED: 'M',
    L: 'L', LARGE: 'L',
    XL: 'XL', XLARGE: 'XL', 'X-LARGE': 'XL', 'X LARGE': 'XL', 'EXTRA LARGE': 'XL',
    XXL: '2XL', '2XL': '2XL', 'XX-LARGE': '2XL', XXLARGE: '2XL',
    XXXL: '3XL', '3XL': '3XL',
    XXXXL: '4XL', '4XL': '4XL',
    XXXXXL: '5XL', '5XL': '5XL',
    '6XL': '6XL'
  };

  // "M (Almost sold out)" and "S(4–6)" both carry their real value in front of
  // the bracket, so anything bracketed is noise for classification purposes.
  const stripNoise = (value) =>
    String(value)
      .replace(/\([^)]*\)/g, ' ')
      .replace(/\[[^\]]*\]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const normaliseApparel = (value) => {
    let s = stripNoise(value).toUpperCase().replace(/\./g, '');
    s = s.replace(/^SIZE\s+/, '').replace(/\s*-\s*/g, '-').trim();
    if (APPAREL_ALIASES[s]) return APPAREL_ALIASES[s];

    // Combined sizes such as "S/M" sort by their smaller half.
    if (s.includes('/')) {
      const first = s.split('/')[0].trim();
      if (APPAREL_ALIASES[first]) return APPAREL_ALIASES[first];
    }
    return null;
  };

  /**
   * Pull the US shoe size out of a label.
   *
   * Order matters: "4 US | 35 EU" must give 4, not 35, so a number written
   * directly before "US" wins over a number written after it.
   */
  const normaliseShoe = (value) => {
    const s = String(value).toUpperCase();

    const before = s.match(/(\d{1,2}(?:[.,]\d)?)\s*US\b/);
    if (before) return parseFloat(before[1].replace(',', '.'));

    const after = s.match(/\bUS[^0-9]{0,10}(\d{1,2}(?:[.,]\d)?)/);
    if (after) return parseFloat(after[1].replace(',', '.'));

    const numbers = (s.match(/\d{1,2}(?:[.,]\d)?/g) || []).map((n) => parseFloat(n.replace(',', '.')));
    const usLike = numbers.find((n) => n >= 3 && n <= 17);
    if (usLike !== undefined) return usLike;

    return null;
  };

  /**
   * Classify a whole option.
   *
   * Returns { kind, sizes } where sizes keeps the ORIGINAL label alongside the
   * canonical value — the original is what has to be clicked to select the
   * variant, so it must never be lost.
   */
  const classify = (values, minMatches = 3) => {
    const apparel = [];
    const shoes = [];

    values.forEach((original) => {
      const a = normaliseApparel(original);
      if (a) apparel.push({ original, key: a, rank: LADDER.indexOf(a) });

      const sh = normaliseShoe(original);
      if (sh !== null) shoes.push({ original, key: String(sh), value: sh });
    });

    // A value like "6" is not apparel, and "XL" is not a shoe, so the two lists
    // rarely overlap. When they do, the bigger, more confident one wins.
    if (apparel.length >= minMatches && apparel.length >= shoes.length) {
      apparel.sort((x, y) => x.rank - y.rank);
      return { kind: 'apparel', sizes: apparel };
    }
    if (shoes.length >= minMatches) {
      shoes.sort((x, y) => x.value - y.value);
      return { kind: 'shoes', sizes: shoes };
    }
    return { kind: 'none', sizes: [] };
  };

  window.JDSize = { LADDER, normaliseApparel, normaliseShoe, classify, stripNoise };
})();
